import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { requirePermission } from '@/lib/permissions/permissions'
import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'
import { leadColumns, LeadRow } from '@/components/leads/leadColumns'
import { Button } from '@/components/ui/button'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { CreateLeadDialog } from '@/components/leads/CreateLeadDialog'
import {
  Plus,
  FileText,
  UserPlus,
  Trash2,
  ClipboardList,
  FilePlus,
} from 'lucide-react'
import NewClientDialog from '@/components/client/newClientDialog'
import { LeadFormResponsesSheet } from '@/components/leads/LeadFormResponsesSheet'
import DeleteAlert from '@/components/DeleteAlert'
import {
  ContextMenuItem,
  ContextMenuSeparator,
} from '@/components/ui/context-menu'
import { toast } from 'sonner'
import { errorToast } from '@/lib/utils/ToastUtils'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import NewFormDialog from '@/components/forms/NewFormDialog'
import { useOrgEntitlements } from '@/lib/hooks/useOrgEntitlements'
import { canUseLeadForms } from '@/lib/billing/plans'
import { Card, CardContent } from '@/components/ui/card'
import { useOrg, useRoleKey } from '@/lib/states/session/hooks'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getLeadStageStyle } from '@/components/leads/leadStageStyles'
import { trpc } from '@/lib/trpc/client'
import { patchListCache, removeFromList } from '@trainrr/api-client'

export const Route = createFileRoute('/_auth/_dashboard/leads/')({
  beforeLoad: () => {
    requirePermission('organisation.manage')
  },
  component: LeadsPage,
})

type LeadGroupBy = 'none' | 'stage' | 'tag'

function areLeadTagsEqual(
  current: LeadRow['tags'],
  next: LeadRow['tags'],
) {
  if (current.length !== next.length) {
    return false
  }

  return current.every((tag, index) => {
    const other = next[index]
    return (
      other !== undefined &&
      tag.id === other.id &&
      tag.name === other.name &&
      tag.color === other.color
    )
  })
}

function applyOptimisticLeadTags(
  leads: LeadRow[],
  optimisticTagsByLeadId: Map<string, LeadRow['tags']>,
) {
  if (optimisticTagsByLeadId.size === 0) {
    return leads
  }

  return leads.map((lead) => {
    const optimisticTags = optimisticTagsByLeadId.get(lead.id)
    if (!optimisticTags || areLeadTagsEqual(lead.tags, optimisticTags)) {
      return lead
    }

    return { ...lead, tags: optimisticTags }
  })
}

function LeadsPage() {
  const navigate = useNavigate()
  const org = useOrg()
  const role = useRoleKey()
  const utils = trpc.useUtils()
  const { data: entitlements } = useOrgEntitlements()
  const allowLeadForms = canUseLeadForms(entitlements)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [rows, setRows] = useState<LeadRow[]>([])
  const [leadToDelete, setLeadToDelete] = useState<LeadRow | null>(null)
  const [responsesLeadId, setResponsesLeadId] = useState<string | null>(null)
  const [convertLead, setConvertLead] = useState<LeadRow | null>(null)
  const [groupBy, setGroupBy] = useState<LeadGroupBy>('none')
  const optimisticTagsByLeadIdRef = useRef(new Map<string, LeadRow['tags']>())

  const {
    data: leadData,
    error: leadsError,
    isLoading,
  } = trpc.leads.list.useQuery(undefined, { enabled: Boolean(org?.id) })
  const { data: stages, error: stagesError } = trpc.leads.listStages.useQuery(
    undefined,
    { enabled: Boolean(org?.id) },
  )
  const stageOptions = stages ?? []
  const leads = useMemo(() => leadData ?? [], [leadData])
  const deleteLead = trpc.leads.delete.useMutation({
    onSuccess: (_data, variables) => {
      patchListCache(utils.leads.list, (old) =>
        removeFromList(old, variables.leadId),
      )
    },
    onError: (error) => {
      console.error(error)
      errorToast(error)
    },
  })

  useLayoutEffect(() => {
    setRows(applyOptimisticLeadTags(leads, optimisticTagsByLeadIdRef.current))
    optimisticTagsByLeadIdRef.current.forEach((tags, leadId) => {
      const lead = leads.find((item) => item.id === leadId)
      if (!lead || areLeadTagsEqual(lead.tags, tags)) {
        optimisticTagsByLeadIdRef.current.delete(leadId)
      }
    })
  }, [leads])

  if (leadsError || stagesError) {
    throw leadsError ?? stagesError
  }

  const isAdmin = role === 'owner' || role === 'admin'
  const columns = useMemo(
    () =>
      leadColumns({
        orgId: org?.id ?? '',
        stages: stageOptions,
        onStageUpdated: (leadId, stageId, stageName) => {
          setRows((current) =>
            current.map((lead) =>
              lead.id === leadId ? { ...lead, stageId, stageName } : lead,
            ),
          )
        },
        onDeleteRequested: (lead) => setLeadToDelete(lead),
        onViewResponse: (lead) => setResponsesLeadId(lead.id),
        onConvertToClient: (lead) => setConvertLead(lead),
        onTagsUpdated: (leadId, tags) => {
          optimisticTagsByLeadIdRef.current.set(leadId, tags)
          setRows((current) =>
            current.map((lead) =>
              lead.id === leadId
                ? areLeadTagsEqual(lead.tags, tags)
                  ? lead
                  : { ...lead, tags }
                : lead,
            ),
          )
        },
      }),
    [org?.id, stageOptions],
  )

  const convertPrefill = useMemo(() => {
    if (!convertLead) return undefined
    const fullName = convertLead.name?.trim() ?? ''
    const nameParts = fullName.split(/\s+/).filter(Boolean)
    const firstName = nameParts[0] ?? ''
    const lastName = nameParts.slice(1).join(' ')
    return {
      firstName,
      lastName,
      email: convertLead.email ?? '',
      phone: convertLead.phone?.trim() ?? '',
    }
  }, [convertLead])

  const handleDeleteLead = async () => {
    if (!leadToDelete) return
    try {
      await deleteLead.mutateAsync({ leadId: leadToDelete.id })
    } catch {
      return
    }
    setLeadToDelete(null)
    toast.success('Lead deleted')
  }

  const stageFilterOptions = useMemo(
    () =>
      stageOptions
        .map((stage) => ({
          label: stage.name ?? 'Untitled',
          value: stage.name ?? 'Untitled',
        }))
        .filter(
          (option, index, all) =>
            all.findIndex((item) => item.value === option.value) === index,
        ),
    [stageOptions],
  )

  const sourceFilterOptions = useMemo(() => {
    const unique = new Set<string>()
    rows.forEach((lead) => {
      if (lead.sourceChannel?.trim()) {
        unique.add(lead.sourceChannel)
      }
    })
    return Array.from(unique)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ label: value, value }))
  }, [rows])

  const tagFilterOptions = useMemo(() => {
    const tagsById = new Map<
      string,
      { label: string; value: string; color: string | null }
    >()

    rows.forEach((lead) => {
      lead.tags.forEach((tag) => {
        tagsById.set(tag.id, {
          label: tag.name,
          value: tag.id,
          color: tag.color,
        })
      })
    })

    return Array.from(tagsById.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    )
  }, [rows])

  const groupedSections = useMemo(() => {
    if (groupBy === 'none') {
      return undefined
    }

    return (visibleRows: LeadRow[]) => {
      if (groupBy === 'stage') {
        const rowsByStage = new Map<string, LeadRow[]>()

        visibleRows.forEach((lead) => {
          const key = lead.stageId ?? 'no-stage'
          if (!rowsByStage.has(key)) {
            rowsByStage.set(key, [])
          }
          rowsByStage.get(key)?.push(lead)
        })

        const grouped: {
          id: string
          label: string
          indicatorColor?: string | null
          rows: LeadRow[]
          defaultOpen?: boolean
        }[] = stageOptions
          .map((stage, index) => ({
            id: stage.id,
            label: stage.name ?? 'Untitled',
            indicatorColor: getLeadStageStyle(index).indicator,
            rows: rowsByStage.get(stage.id) ?? [],
            defaultOpen: true,
          }))
          .filter((section) => section.rows.length > 0)

        const ungroupedRows = rowsByStage.get('no-stage') ?? []
        if (ungroupedRows.length > 0) {
          grouped.push({
            id: 'no-stage',
            label: 'No stage',
            rows: ungroupedRows,
            defaultOpen: true,
          })
        }

        return grouped
      }

      const rowsByTag = new Map<string, LeadRow[]>()
      visibleRows.forEach((lead) => {
        if (lead.tags.length === 0) {
          if (!rowsByTag.has('no-tag')) {
            rowsByTag.set('no-tag', [])
          }
          rowsByTag.get('no-tag')?.push(lead)
          return
        }

        lead.tags.forEach((tag) => {
          if (!rowsByTag.has(tag.id)) {
            rowsByTag.set(tag.id, [])
          }
          rowsByTag.get(tag.id)?.push(lead)
        })
      })

      const grouped: {
        id: string
        label: string
        indicatorColor?: string | null
        rows: LeadRow[]
        defaultOpen?: boolean
      }[] = Array.from(rowsByTag.entries())
        .map(([id, groupRows]) => {
          const groupedTag =
            id === 'no-tag'
              ? null
              : groupRows
                  .flatMap((lead) => lead.tags)
                  .find((tag) => tag.id === id)

          return {
            id,
            label: groupedTag?.name ?? 'No tags',
            indicatorColor: groupedTag?.color ?? null,
            rows: groupRows,
            defaultOpen: true,
          }
        })
        .sort((left, right) => {
          if (left.id === 'no-tag') return 1
          if (right.id === 'no-tag') return -1
          return left.label.localeCompare(right.label)
        })

      return grouped
    }
  }, [groupBy, stageOptions])

  const groupByControl = (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Group by
      </span>
      <Select
        value={groupBy}
        onValueChange={(value) => setGroupBy(value as LeadGroupBy)}
      >
        <SelectTrigger className="h-8 w-[160px]">
          <SelectValue placeholder="None" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">None</SelectItem>
          <SelectItem value="stage">Stage</SelectItem>
          <SelectItem value="tag">Tag</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
      <DeleteAlert
        open={!!leadToDelete}
        title={
          leadToDelete
            ? `Delete ${leadToDelete.name ?? 'lead'}?`
            : 'Delete lead?'
        }
        description="This will permanently remove the lead."
        onCancel={() => setLeadToDelete(null)}
        onDelete={handleDeleteLead}
      />
      <div className="flex h-full min-h-0 min-w-0 flex-col">
        <div className="flex min-h-0 flex-1 flex-col gap-4 min-w-0 p-8 pt-6">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-3xl font-bold tracking-tight">Leads</h2>
            {isAdmin ? (
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="mr-2" /> New lead
              </Button>
            ) : null}
          </div>
          {!allowLeadForms && (
            <Card>
              <CardContent className="flex flex-col gap-2 p-4 text-sm">
                <p className="font-medium">Lead forms require Growth</p>
                <p className="text-muted-foreground">
                  Upgrade to Growth or above to create and use lead capture
                  forms.
                </p>
                <div>
                  <Button asChild size="sm">
                    <Link to="/upgrade" search={{ plan: 'growth_monthly' }}>
                      View upgrade options
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <VirtualizedDataTable
              className="min-h-0 flex-1"
              columns={columns}
              data={rows}
              onRowSelected={(lead) =>
                navigate({ to: '/leads/$leadId', params: { leadId: lead.id } })
              }
              emptyView={
                <Empty className="py-12">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <ClipboardList className="size-6" />
                    </EmptyMedia>
                    <EmptyTitle>No Leads Yet</EmptyTitle>
                    <EmptyDescription>
                      Add your first lead to get started. Or create a lead form
                      to capture leads automatically.
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button
                      onClick={() => {
                        setDialogOpen(true)
                      }}
                    >
                      <UserPlus className="mr-2" /> Add Lead
                    </Button>
                    <Button
                      onClick={() => {
                        if (!allowLeadForms) {
                          navigate({
                            to: '/upgrade',
                            search: { plan: 'growth_monthly' },
                          })
                          return
                        }
                        setFormDialogOpen(true)
                      }}
                    >
                      <FilePlus className="mr-2" />
                      Create lead form
                    </Button>
                  </EmptyContent>
                </Empty>
              }
              renderRowContextMenu={(lead) => (
                <>
                  {lead.respondedAt ? (
                    <ContextMenuItem
                      onSelect={() => setResponsesLeadId(lead.id)}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      View response
                    </ContextMenuItem>
                  ) : null}
                  <ContextMenuItem onSelect={() => setConvertLead(lead)}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Convert to client
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                  <ContextMenuItem
                    variant="destructive"
                    onSelect={() => setLeadToDelete(lead)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete lead
                  </ContextMenuItem>
                </>
              )}
              showSearchBar
              filterOptions={[
                {
                  column: 'stageName',
                  title: 'Stage',
                  singleSelect: true,
                  includeAllOption: true,
                  options: stageFilterOptions,
                },
                {
                  column: 'sourceChannel',
                  title: 'Source',
                  singleSelect: true,
                  includeAllOption: true,
                  options: sourceFilterOptions,
                },
                {
                  column: 'tags',
                  title: 'Tags',
                  options: tagFilterOptions,
                },
              ]}
              searchBarActions={groupByControl}
              groupedSections={groupedSections}
              initialSortColumn="createdAt"
              initialSortDesc
              isLoading={!org || isLoading}
              stickyHeader
              tableContainerClassName="min-h-0 flex-1"
              tableWrapperClassName="h-full min-h-0"
              tableClassName="table-fixed min-w-[1420px]"
              virtualizedHeight="100%"
              estimateRowHeight={68}
            />
          </div>
        </div>
      </div>
      <CreateLeadDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onLeadCreated={(leadId) =>
          navigate({ to: '/leads/$leadId', params: { leadId } })
        }
      />
      <NewFormDialog
        open={formDialogOpen && allowLeadForms}
        onOpenChange={setFormDialogOpen}
        formType="lead"
        forceFormType
      />

      <LeadFormResponsesSheet
        leadId={responsesLeadId ?? ''}
        open={!!responsesLeadId}
        onOpenChange={(open) => {
          if (!open) setResponsesLeadId(null)
        }}
      />
      <NewClientDialog
        open={!!convertLead}
        onOpenChange={(open) => {
          if (!open) setConvertLead(null)
        }}
        prefill={convertPrefill}
      />
    </div>
  )
}
